
import React from 'react'
import i18n from 'i18n'

import store from 'store'
import { profilePush } from 'actions/index'
import sanitizeAndLinkifyHtml from 'modules/sanitizeAndLinkifyHtml'
import { formatNumeric } from 'lib/helpers'
import { formatCurrency } from 'utils/money'

import { TertiaryButton, colors, spacing } from '@PipelineDeals/shared-react-components'

import styled from 'styled-components'
import { formatDate } from 'utils/date'
const StyledTertiaryButton = styled(TertiaryButton)`
  &&& {
   color: ${({ theme }) => theme.colors.blue[600]};
   padding-top: ${spacing.md}
  }
`

const noneRenderer = () => {
  return <span style={{ color: colors.gray[400], fontStyle: 'italic' }}>{i18n.t('common.none')}</span>
}

const TRUNCATE_LENGTH = 120

const textFormatter = ({
  customFieldLabel,
  custom_fields: customFields,
  setShowMoreHash,
  showMoreHash
}) => {
  const id = `custom_label_${customFieldLabel.id}`
  let value = (customFields[id] || '')
  const isTooLong = value.length > TRUNCATE_LENGTH
  const showMore = showMoreHash[id] || false
  const shouldTruncate = isTooLong && !showMore
  value = shouldTruncate ? value.slice(0, TRUNCATE_LENGTH) + '...' : value
  if (!value.length) return noneRenderer()
  const htmlString = sanitizeAndLinkifyHtml(value)
  return (
    <>
      <div style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: htmlString }} />
      {isTooLong && (
        <StyledTertiaryButton
          onClick={() => { setShowMoreHash({ ...showMoreHash, [id]: !showMore }) }}
          content={showMore ? i18n.t('common.showLess') : i18n.t('common.showMore')}
          trailingContent={showMore ? <i className="fas fa-chevron-up" /> : <i className="fas fa-chevron-down" />}
          padded={false}
        />
      )}
    </>
  )
}

const dateFormatter = ({
  customFieldLabel,
  custom_fields: customFields
}) => {
  const id = `custom_label_${customFieldLabel.id}`
  const value = customFields[id]
  if (value) {
    return formatDate(value)
  } else {
    return noneRenderer()
  }
}

const numericFormatter = ({
  customFieldLabel,
  custom_fields: customFields
}) => {
  const id = `custom_label_${customFieldLabel.id}`
  const value = customFields[id]
  if (value) {
    return formatNumeric(value)
  } else {
    return noneRenderer()
  }
}

const currencyFormatter = ({
  currentAccount,
  currentUser,
  customFieldLabel,
  custom_fields: customFields,
  currency_id: currencyId
}) => {
  const id = `custom_label_${customFieldLabel.id}`
  const value = customFields[id]

  if (value) {
    const currency = currentAccount.selected_currencies.find(c => c.id === currencyId) || currentUser.currency
    return formatCurrency(
      value,
      currency ? currency.symbol : undefined
    )
  } else {
    return noneRenderer()
  }
}

const selectFormatter = ({
  customFieldLabel,
  custom_fields: customFields
}) => {
  const id = `custom_label_${customFieldLabel.id}`
  let value = customFields[id]

  if ([null, undefined].includes(value)) return noneRenderer()
  value = Array.of(value).flat().filter(Boolean)

  if (!value.length) return noneRenderer()

  const entry = customFieldLabel.custom_field_label_dropdown_entries.filter(entry =>
    value.includes(entry.id)
  )
  if (!entry) return noneRenderer()

  return entry.map(e => e.name).join(', ')
}

const associationFormatter = ({
  customFieldLabel,
  custom_fields: customFields
}) => {
  const id = `custom_label_${customFieldLabel.id}`
  let value = customFields[id]

  if ([null, undefined].includes(value)) return noneRenderer()
  value = Array.of(value).flat().filter(Boolean)

  if (!value.length) return noneRenderer()
  if (window.User.external) {
    return value.map(v => v.split(':')[0]).join(', ')
  }

  return value.map(v => {
    const displayValue = v.split(':')[0]
    const id = v.split(':')[1]
    return <><a key={id} onClick={e => { e.stopPropagation(); store.dispatch(profilePush({ type: 'Company', id, event: e })) }} href={`/companies/${id}`}>{displayValue}</a></>
  }).map((link, index) => {
    return index < value.length - 1 ? [link, ', '] : [link]
  }).reduce((a, b) => [...a, ...b], [])
}

const booleanFormatter = ({
  customFieldLabel,
  custom_fields: customFields
}) => {
  const id = `custom_label_${customFieldLabel.id}`
  const value = customFields[id]
  switch (value) {
    case true:
      return i18n.t('common.true')
    case false:
      return i18n.t('common.false')
    default:
      return noneRenderer()
  }
}

const calculatedFormatter = ({
  customFieldLabel,
  custom_fields: customFields
}) => {
  const id = `custom_label_${customFieldLabel.id}`
  const value = customFields[id]
  return value || noneRenderer()
}

const formatForTable = {
  text: textFormatter,
  date: dateFormatter,
  numeric: numericFormatter,
  currency: currencyFormatter,
  forecast: currencyFormatter,
  dropdown: selectFormatter,
  multi_select: selectFormatter,
  single_association: associationFormatter,
  multi_association: associationFormatter,
  boolean: booleanFormatter,
  calculated: calculatedFormatter
}

export const customFieldRenderer = ({ customFieldLabel, customFields, currentAccount, currentUser, setShowMoreHash, showMoreHash }) => {
  const formatter = formatForTable[customFieldLabel.output_type || customFieldLabel.field_type]

  if (!formatter) return null
  return formatter({
    customFieldLabel,
    custom_fields: customFields,
    currentAccount,
    currentUser,
    currency_id: customFieldLabel.currency_id,
    setShowMoreHash,
    showMoreHash
  })
}

export default customFieldRenderer
